import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../context/I18nContext';
import './VehicleLookup.css';

function VehicleLookup({ lookupRequest, initialTab = 'lookup', showInternalNav = true }) {
  const { apiFetch, hasPermission } = useAuth();
  const { language } = useI18n();
  const [searchType, setSearchType] = useState('plate');
  const [searchValue, setSearchValue] = useState('');
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [deletingPlate, setDeletingPlate] = useState(null);
  const [expandedStorage, setExpandedStorage] = useState({});
  const [activeTab, setActiveTab] = useState('lookup');
  const [modelSearch, setModelSearch] = useState('');
  const [modelResults, setModelResults] = useState([]);
  const [modelLoading, setModelLoading] = useState(false);
  const [modelError, setModelError] = useState(null);
  const [itemEditValues, setItemEditValues] = useState({});
  const [itemUpdateLoadingKey, setItemUpdateLoadingKey] = useState(null);
  const [itemUpdateError, setItemUpdateError] = useState(null);

  const isStored = (vehicle) => {
    if (typeof vehicle?.isStored === 'boolean') return vehicle.isStored;
    return Number(vehicle?.stored) === 0;
  };

  const getVehicleModelDisplay = (vehicle) => {
    const label = String(vehicle?.modelLabel || '').trim();
    const source = String(vehicle?.modelSource || '').trim();

    let rawModel = vehicle?.model;
    if ((rawModel === undefined || rawModel === null || rawModel === '') && typeof vehicle?.vehicle === 'string') {
      try {
        const parsedVehicleData = JSON.parse(vehicle.vehicle);
        rawModel = parsedVehicleData?.model;
      } catch (e) {
        rawModel = null;
      }
    }

    // Defensive fallback: some payloads may put JSON into model by mistake.
    if (typeof rawModel === 'string') {
      const trimmed = rawModel.trim();
      if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
        try {
          const parsedModelData = JSON.parse(trimmed);
          rawModel = parsedModelData?.model ?? null;
        } catch (e) {
          rawModel = null;
        }
      }
    }

    const sourceLabels = {
      native_available: language === 'da' ? 'Native' : 'Native',
      native_unresolved: language === 'da' ? 'Native fallback' : 'Native fallback',
      native_missing: language === 'da' ? 'Native mangler' : 'Native missing',
      client_native: language === 'da' ? 'Client native' : 'Client native',
      client_unresolved: language === 'da' ? 'Client fallback' : 'Client fallback',
      modded_fallback: language === 'da' ? 'Mod fallback' : 'Mod fallback',
      spawn_name: language === 'da' ? 'Spawn navn' : 'Spawn name'
    };

    if (label) {
      return {
        primary: label,
        secondary: rawModel !== undefined && rawModel !== null && rawModel !== '' && String(rawModel) !== label
          ? `hash: ${rawModel}`
          : null,
        source: sourceLabels[source] || null
      };
    }

    if (rawModel === undefined || rawModel === null || rawModel === '') {
      return {
        primary: t.unknown,
        secondary: null,
        source: sourceLabels[source] || null
      };
    }

    if (typeof rawModel === 'number' || /^-?\d+$/.test(String(rawModel))) {
      return {
        primary: `hash: ${rawModel}`,
        secondary: null,
        source: sourceLabels[source] || null
      };
    }

    return {
      primary: String(rawModel),
      secondary: null,
      source: sourceLabels[source] || null
    };
  };

  const t = language === 'da'
    ? {
        noVehicles: 'Ingen køretøjer fundet',
        failedLookup: 'Kunne ikke slå køretøj op',
        confirmDelete: 'Er du sikker på, at du vil slette køretøj med nummerplade {plate}? Denne handling kan ikke fortrydes!',
        failedDelete: 'Kunne ikke slette køretøj',
        deletedSuccess: 'køretøj {plate} blev slettet',
        title: 'køretøj opslag',
        subtitle: 'søg efter køretøjer via nummerplade eller ejer-identifikator',
        byPlate: 'Via nummerplade',
        byOwner: 'Via ejer',
        plateNumber: 'Nummerplade',
        ownerIdentifier: 'Ejer identifikator',
        enterPlate: 'Indtast nummerplade',
        enterIdentifier: 'Indtast spiller identifikator',
        searching: 'søger...',
        searchVehicles: 'søg køretøjer',
        foundVehicles: 'Fandt {count} køretøj(er)',
        stored: 'Lagret',
        out: 'Ude',
        vehicleInfo: 'køretøjsinformation',
        owner: 'Ejer:',
        model: 'Model:',
        type: 'Type:',
        garage: 'Garage:',
        stats: 'køretøjsstatistik',
        fuel: 'brændstof:',
        engine: 'Motor:',
        body: 'Karosseri:',
        glovebox: 'Handskerum',
        trunk: 'Bagagerum',
        items: 'items',
        showLess: 'Vis mindre',
        showAll: 'Vis alle {count} items',
        deleting: 'Sletter...',
        deleteVehicle: 'Slet køretøj',
        unknown: 'Ukendt',
        na: 'N/A',
        byModel: 'Via model',
        modelOrHash: 'Model / hash',
        enterModelOrHash: 'Indtast modelnavn eller hash',
        searchByModel: 'Søg via model',
        foundOwners: 'Fandt {count} ejer(e)',
        vehiclesLabel: 'køretøjer',
        noVehiclesForModel: 'Ingen køretøjer fundet for denne model',
        platesLabel: 'Plader',
        itemAmount: 'Antal',
        updateItem: 'Opdater',
        addItem: 'Tilføj',
        removeItem: 'Fjern',
        failedItemUpdate: 'Kunne ikke opdatere item i køretøj'
      }
    : {
        noVehicles: 'No vehicles found',
        failedLookup: 'Failed to lookup vehicle',
        confirmDelete: 'Are you sure you want to delete vehicle with plate {plate}? This action cannot be undone!',
        failedDelete: 'Failed to delete vehicle',
        deletedSuccess: 'Vehicle {plate} deleted successfully',
        title: 'Vehicle Lookup',
        subtitle: 'Search for vehicles by plate or owner identifier',
        byPlate: 'By Plate',
        byOwner: 'By Owner',
        plateNumber: 'Plate Number',
        ownerIdentifier: 'Owner Identifier',
        enterPlate: 'Enter plate number',
        enterIdentifier: 'Enter player identifier',
        searching: 'Searching...',
        searchVehicles: 'Search Vehicles',
        foundVehicles: 'Found {count} vehicle(s)',
        stored: 'Stored',
        out: 'Out',
        vehicleInfo: 'Vehicle Information',
        owner: 'Owner:',
        model: 'Model:',
        type: 'Type:',
        garage: 'Garage:',
        stats: 'Vehicle Stats',
        fuel: 'Fuel:',
        engine: 'Engine:',
        body: 'Body:',
        glovebox: 'Glovebox',
        trunk: 'Trunk',
        items: 'items',
        showLess: 'Show less',
        showAll: 'Show all {count} items',
        deleting: 'Deleting...',
        deleteVehicle: 'Delete Vehicle',
        unknown: 'Unknown',
        na: 'N/A',
        byModel: 'By Model',
        modelOrHash: 'Model / Hash',
        enterModelOrHash: 'Enter model name or hash',
        searchByModel: 'Search by Model',
        foundOwners: 'Found {count} owner(s)',
        vehiclesLabel: 'vehicles',
        noVehiclesForModel: 'No vehicles found for that model',
        platesLabel: 'Plates',
        itemAmount: 'Amount',
        updateItem: 'Set',
        addItem: 'Add',
        removeItem: 'Remove',
        failedItemUpdate: 'Failed to update vehicle item'
      };

  const text = (template, params = {}) => template.replace(/\{(\w+)\}/g, (_, key) => params[key] ?? `{${key}}`);

  const isStorageExpanded = (plate, storageType) => {
    return Boolean(expandedStorage[`${plate}:${storageType}`]);
  };

  const toggleStorageExpanded = (plate, storageType) => {
    const key = `${plate}:${storageType}`;
    setExpandedStorage((prev) => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const normalizeItemName = (item) => String(item?.name || item?.label || '').trim();

  const getVehicleItemInputKey = (plate, storageType, item) => {
    return `${plate}:${storageType}:${normalizeItemName(item).toLowerCase()}`;
  };

  const getVehicleItemCount = (item) => {
    const countValue = Number(item?.count ?? item?.amount ?? 0);
    return Number.isFinite(countValue) ? countValue : 0;
  };

  const handleVehicleItemAmountChange = (plate, storageType, item, value) => {
    const key = getVehicleItemInputKey(plate, storageType, item);
    setItemEditValues((prev) => ({
      ...prev,
      [key]: value
    }));
  };

  const applyVehicleItemCountUpdate = (plate, storageType, itemName, newCount) => {
    setVehicles((prevVehicles) => prevVehicles.map((vehicle) => {
      if (String(vehicle?.plate || '').toUpperCase() !== String(plate || '').toUpperCase()) {
        return vehicle;
      }

      const currentList = Array.isArray(vehicle?.[storageType]) ? vehicle[storageType] : [];
      let wasFound = false;
      const updatedList = currentList
        .map((entry) => {
          const entryName = normalizeItemName(entry);
          if (!entryName || entryName.toLowerCase() !== String(itemName || '').toLowerCase()) {
            return entry;
          }

          wasFound = true;
          if (newCount <= 0) {
            return null;
          }

          return {
            ...entry,
            count: newCount
          };
        })
        .filter(Boolean);

      if (!wasFound && newCount > 0) {
        updatedList.push({
          name: itemName,
          label: itemName,
          count: newCount
        });
      }

      return {
        ...vehicle,
        [storageType]: updatedList
      };
    }));
  };

  const updateVehicleStorageItem = async (plate, storageType, item, action) => {
    const itemName = normalizeItemName(item);
    const itemKey = getVehicleItemInputKey(plate, storageType, item);
    const rawAmount = itemEditValues[itemKey] ?? '1';
    const parsedAmount = Number(rawAmount);
    const amount = Number.isFinite(parsedAmount) ? parsedAmount : 0;

    if (!itemName || amount <= 0) {
      setItemUpdateError(t.failedItemUpdate);
      return;
    }

    setItemUpdateError(null);
    setItemUpdateLoadingKey(itemKey);

    try {
      const response = await apiFetch('/api/items/update', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          storageType,
          storageId: plate,
          item: itemName,
          action,
          amount
        })
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.error || t.failedItemUpdate);
      }

      const nextCount = Number(payload?.newCount);
      applyVehicleItemCountUpdate(plate, storageType, itemName, Number.isFinite(nextCount) ? nextCount : getVehicleItemCount(item));
    } catch (err) {
      setItemUpdateError(err.message || t.failedItemUpdate);
    } finally {
      setItemUpdateLoadingKey(null);
    }
  };

  const runSearch = async (type, value) => {
    const trimmedValue = String(value || '').trim();
    if (!trimmedValue) return;

    setLoading(true);
    setError(null);
    setVehicles([]);

    try {
      const param = type === 'plate'
        ? `plate=${encodeURIComponent(trimmedValue)}`
        : `identifier=${encodeURIComponent(trimmedValue)}`;
      const response = await apiFetch(`/api/vehicles/lookup?${param}`);
      
      if (response.status === 404) {
        setError(t.noVehicles);
        setLoading(false);
        return;
      }

      if (!response.ok) {
        throw new Error(t.failedLookup);
      }

      const data = await response.json();
      setVehicles(Array.isArray(data) ? data : [data]);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    await runSearch(searchType, searchValue);
  };

  const handleModelSearch = async (e) => {
    e.preventDefault();
    const input = String(modelSearch || '').trim();
    if (!input) return;
    setModelLoading(true);
    setModelError(null);
    setModelResults([]);
    try {
      const response = await apiFetch(`/api/vehicles/by-model?model=${encodeURIComponent(input)}`);
      if (response.status === 404) {
        setModelError(t.noVehiclesForModel);
        return;
      }
      if (!response.ok) throw new Error(t.failedLookup);
      const data = await response.json();
      setModelResults(Array.isArray(data) ? data : []);
    } catch (err) {
      setModelError(err.message);
    } finally {
      setModelLoading(false);
    }
  };

  useEffect(() => {
    if (!lookupRequest?.requestId) return;

    const nextType = lookupRequest.searchType || 'plate';
    const nextValue = lookupRequest.searchValue || '';

    setSearchType(nextType);
    setSearchValue(nextValue);
    runSearch(nextType, nextValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lookupRequest?.requestId]);

  useEffect(() => {
    const nextTab = String(initialTab || '').trim().toLowerCase();
    if (nextTab === 'lookup' || nextTab === 'model') {
      setActiveTab(nextTab);
    }
  }, [initialTab]);

  const handleDelete = async (plate) => {
    if (!window.confirm(text(t.confirmDelete, { plate }))) {
      return;
    }

    setDeletingPlate(plate);
    setError(null);

    try {
      const response = await apiFetch(`/api/vehicles/${encodeURIComponent(plate)}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || t.failedDelete);
      }

      // Remove the deleted vehicle from the list
      setVehicles(vehicles.filter(v => v.plate !== plate));
      
      // Show success message briefly
      const successMsg = text(t.deletedSuccess, { plate });
      setError(null);
      alert(successMsg);
    } catch (err) {
      setError(err.message);
    } finally {
      setDeletingPlate(null);
    }
  };

  return (
    <div className="vehicle-lookup">
      <div className="lookup-header">
        <h2>{t.title}</h2>
        <p>{t.subtitle}</p>
      </div>

      {showInternalNav && (
        <div className="tab-switcher">
          <button
            type="button"
            className={activeTab === 'lookup' ? 'active' : ''}
            onClick={() => setActiveTab('lookup')}
          >
            {t.byPlate} / {t.byOwner}
          </button>
          <button
            type="button"
            className={activeTab === 'model' ? 'active' : ''}
            onClick={() => setActiveTab('model')}
          >
            {t.byModel}
          </button>
        </div>
      )}

      {activeTab === 'lookup' && (<>
      <form onSubmit={handleSearch} className="lookup-form">
        <div className="search-type-selector">
          <button
            type="button"
            className={searchType === 'plate' ? 'active' : ''}
            onClick={() => setSearchType('plate')}
          >
            {t.byPlate}
          </button>
          <button
            type="button"
            className={searchType === 'identifier' ? 'active' : ''}
            onClick={() => setSearchType('identifier')}
          >
            {t.byOwner}
          </button>
        </div>

        <div className="form-group">
          <label>{searchType === 'plate' ? t.plateNumber : t.ownerIdentifier}</label>
          <input
            type="text"
            placeholder={searchType === 'plate' ? t.enterPlate : t.enterIdentifier}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            required
          />
        </div>

        <button type="submit" className="search-button" disabled={loading}>
          {loading ? t.searching : `${t.searchVehicles}`}
        </button>
      </form>

      {error && (
        <div className="alert error">
          <strong>{error}</strong>
        </div>
      )}

      {itemUpdateError && (
        <div className="alert error">
          <strong>{itemUpdateError}</strong>
        </div>
      )}

      {vehicles.length > 0 && (
        <div className="results-section">
          <h3>{text(t.foundVehicles, { count: vehicles.length })}</h3>
          
          <div className="vehicles-grid">
            {vehicles.map((vehicle, index) => (
              (() => {
                const modelDisplay = getVehicleModelDisplay(vehicle);
                return (
              <div key={index} className="vehicle-card">
                <div className="vehicle-header">
                  <div className="plate">{vehicle.plate}</div>
                  <div className="status-badges">
                    {isStored(vehicle) ? (
                      <span className="badge stored">{t.stored}</span>
                    ) : (
                      <span className="badge not-stored">{t.out}</span>
                    )}
                  </div>
                </div>

                <div className="vehicle-body">
                  <div className="info-section">
                    <h4>{t.vehicleInfo}</h4>
                    <div className="info-item">
                      <span className="label">{t.owner}</span>
                      <span className="value mono">{vehicle.owner}</span>
                    </div>
                    <div className="info-item">
                      <span className="label">{t.model}</span>
                      <span className="value model-value">
                        <span className="model-primary">{modelDisplay.primary}</span>
                        {modelDisplay.secondary && <span className="model-secondary mono">{modelDisplay.secondary}</span>}
                      </span>
                    </div>
                    {vehicle.type && (
                      <div className="info-item">
                        <span className="label">{t.type}</span>
                        <span className="value">{vehicle.type}</span>
                      </div>
                    )}
                    {vehicle.garage && (
                      <div className="info-item">
                        <span className="label">{t.garage}</span>
                        <span className="value">{vehicle.garage}</span>
                      </div>
                    )}
                  </div>

                  <div className="info-section">
                    <h4>{t.stats}</h4>
                    {vehicle.fuel !== undefined && (
                      <div className="info-item">
                        <span className="label">{t.fuel}</span>
                        <span className="value">{vehicle.fuel}%</span>
                      </div>
                    )}
                    {vehicle.engine_health !== undefined && (
                      <div className="info-item">
                        <span className="label">{t.engine}</span>
                        <span className="value">{Math.round(vehicle.engine_health / 10)}%</span>
                      </div>
                    )}
                    {vehicle.body_health !== undefined && (
                      <div className="info-item">
                        <span className="label">{t.body}</span>
                        <span className="value">{Math.round(vehicle.body_health / 10)}%</span>
                      </div>
                    )}
                  </div>

                  {vehicle.glovebox && vehicle.glovebox.length > 0 && (
                    <div className="info-section">
                      <h4>{t.glovebox} ({Array.isArray(vehicle.glovebox) ? vehicle.glovebox.length : 0} {t.items})</h4>
                      <div className="storage-items">
                        {Array.isArray(vehicle.glovebox) && (isStorageExpanded(vehicle.plate, 'glovebox')
                          ? vehicle.glovebox
                          : vehicle.glovebox.slice(0, 3)
                        ).map((item, i) => (
                          <div key={i} className="storage-item">
                            <span>{item.label || item.name} x{item.count || 1}</span>
                            {hasPermission('items.edit') && (
                              <div className="storage-item-controls">
                                <input
                                  type="number"
                                  min="1"
                                  className="item-amount-input"
                                  value={itemEditValues[getVehicleItemInputKey(vehicle.plate, 'glovebox', item)] ?? '1'}
                                  onChange={(e) => handleVehicleItemAmountChange(vehicle.plate, 'glovebox', item, e.target.value)}
                                  aria-label={t.itemAmount}
                                />
                                <button
                                  type="button"
                                  className="item-update-btn"
                                  disabled={itemUpdateLoadingKey === getVehicleItemInputKey(vehicle.plate, 'glovebox', item)}
                                  onClick={() => updateVehicleStorageItem(vehicle.plate, 'glovebox', item, 'set')}
                                >
                                  {t.updateItem}
                                </button>
                                <button
                                  type="button"
                                  className="item-update-btn"
                                  disabled={itemUpdateLoadingKey === getVehicleItemInputKey(vehicle.plate, 'glovebox', item)}
                                  onClick={() => updateVehicleStorageItem(vehicle.plate, 'glovebox', item, 'add')}
                                >
                                  +
                                </button>
                                <button
                                  type="button"
                                  className="item-update-btn danger"
                                  disabled={itemUpdateLoadingKey === getVehicleItemInputKey(vehicle.plate, 'glovebox', item)}
                                  onClick={() => updateVehicleStorageItem(vehicle.plate, 'glovebox', item, 'remove')}
                                >
                                  -
                                </button>
                              </div>
                            )}
                          </div>
                        ))}
                        {vehicle.glovebox.length > 3 && (
                          <button
                            type="button"
                            className="show-all-items-btn"
                            onClick={() => toggleStorageExpanded(vehicle.plate, 'glovebox')}
                          >
                            {isStorageExpanded(vehicle.plate, 'glovebox')
                              ? t.showLess
                              : text(t.showAll, { count: vehicle.glovebox.length })}
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {vehicle.trunk && vehicle.trunk.length > 0 && (
                    <div className="info-section">
                      <h4>{t.trunk} ({Array.isArray(vehicle.trunk) ? vehicle.trunk.length : 0} {t.items})</h4>
                      <div className="storage-items">
                        {Array.isArray(vehicle.trunk) && (isStorageExpanded(vehicle.plate, 'trunk')
                          ? vehicle.trunk
                          : vehicle.trunk.slice(0, 3)
                        ).map((item, i) => (
                          <div key={i} className="storage-item">
                            <span>{item.label || item.name} x{item.count || 1}</span>
                            {hasPermission('items.edit') && (
                              <div className="storage-item-controls">
                                <input
                                  type="number"
                                  min="1"
                                  className="item-amount-input"
                                  value={itemEditValues[getVehicleItemInputKey(vehicle.plate, 'trunk', item)] ?? '1'}
                                  onChange={(e) => handleVehicleItemAmountChange(vehicle.plate, 'trunk', item, e.target.value)}
                                  aria-label={t.itemAmount}
                                />
                                <button
                                  type="button"
                                  className="item-update-btn"
                                  disabled={itemUpdateLoadingKey === getVehicleItemInputKey(vehicle.plate, 'trunk', item)}
                                  onClick={() => updateVehicleStorageItem(vehicle.plate, 'trunk', item, 'set')}
                                >
                                  {t.updateItem}
                                </button>
                                <button
                                  type="button"
                                  className="item-update-btn"
                                  disabled={itemUpdateLoadingKey === getVehicleItemInputKey(vehicle.plate, 'trunk', item)}
                                  onClick={() => updateVehicleStorageItem(vehicle.plate, 'trunk', item, 'add')}
                                >
                                  +
                                </button>
                                <button
                                  type="button"
                                  className="item-update-btn danger"
                                  disabled={itemUpdateLoadingKey === getVehicleItemInputKey(vehicle.plate, 'trunk', item)}
                                  onClick={() => updateVehicleStorageItem(vehicle.plate, 'trunk', item, 'remove')}
                                >
                                  -
                                </button>
                              </div>
                            )}
                          </div>
                        ))}
                        {vehicle.trunk.length > 3 && (
                          <button
                            type="button"
                            className="show-all-items-btn"
                            onClick={() => toggleStorageExpanded(vehicle.plate, 'trunk')}
                          >
                            {isStorageExpanded(vehicle.plate, 'trunk')
                              ? t.showLess
                              : text(t.showAll, { count: vehicle.trunk.length })}
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {hasPermission('vehicles.edit') && (
                    <div className="vehicle-actions">
                      <button 
                        onClick={() => handleDelete(vehicle.plate)}
                        className="delete-vehicle-btn"
                        disabled={deletingPlate === vehicle.plate}
                      >
                        {deletingPlate === vehicle.plate ? `🗑️ ${t.deleting}` : `🗑️ ${t.deleteVehicle}`}
                      </button>
                    </div>
                  )}
                </div>
              </div>
                );
              })()
            ))}
          </div>
        </div>
      )}
      </>)}

      {activeTab === 'model' && (
        <>
          <form onSubmit={handleModelSearch} className="lookup-form">
            <div className="form-group">
              <label>{t.modelOrHash}</label>
              <input
                type="text"
                placeholder={t.enterModelOrHash}
                value={modelSearch}
                onChange={(e) => setModelSearch(e.target.value)}
                required
              />
            </div>
            <button type="submit" className="search-button" disabled={modelLoading}>
              {modelLoading ? t.searching : `${t.searchByModel}`}
            </button>
          </form>

          {modelError && (
            <div className="alert error">
              <strong>{modelError}</strong>
            </div>
          )}

          {modelResults.length > 0 && (
            <div className="results-section">
              <h3>{text(t.foundOwners, { count: modelResults.length })}</h3>
              <div className="model-results-list">
                {modelResults.map((entry, idx) => (
                  <div key={idx} className="model-result-card">
                    <div className="model-result-header">
                      <span className="model-result-name">{entry.name || entry.owner}</span>
                      <span className="model-result-count">{entry.count} {t.vehiclesLabel}</span>
                    </div>
                    {entry.name && (
                      <div className="model-result-identifier mono">{entry.owner}</div>
                    )}
                    <div className="model-result-plates">
                      {entry.plates.map((p, pi) => (
                        <span key={pi} className="model-plate-tag">{p}</span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default VehicleLookup;



